<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!-- Start:: Referral Code Card -->
      <div class="referral_code_card mb-4">
        <div class="card_header">
          <h5 class="text-white">{{ $t("PLACEHOLDERS.myReferralCode") }}</h5>
        </div>
        <div class="card_body">
          <div class="row align-items-center">
            <!-- Referral Code Display -->
            <div class="col-md-6">
              <div class="code_display_wrapper">
                <label class="form-label">{{
                  $t("PLACEHOLDERS.referralCode")
                }}</label>
                <div class="code_box">
                  <input
                    type="text"
                    :value="referralCodeData.code"
                    readonly
                    class="form-control"
                    id="referralCodeInput"
                  />
                  <button
                    class="copy_btn"
                    @click="copyReferralCode"
                    :title="$t('PLACEHOLDERS.copy')"
                  >
                    <i :class="codeCopied ? 'fal fa-check' : 'fal fa-copy'"></i>
                  </button>
                </div>
                <small class="mt-2 d-block">
                  {{ $t("PLACEHOLDERS.shareCodeWithOthers") }}
                </small>
              </div>
            </div>

            <!-- Points Summary -->
            <div class="col-md-6">
              <div class="points_summary">
                <div class="summary_item">
                  <i class="fal fa-star text-warning fs-4"></i>
                  <div class="summary_text">
                    <span class="label">{{
                      $t("PLACEHOLDERS.totalPoints")
                    }}</span>
                    <span class="value">{{
                      referralCodeData.total_points || 0
                    }}</span>
                  </div>
                </div>
                <div class="summary_item mt-3">
                  <i class="fal fa-users text-primary fs-4"></i>
                  <div class="summary_text">
                    <span class="label">{{
                      $t("PLACEHOLDERS.totalReferrals")
                    }}</span>
                    <span class="value">{{
                      referralCodeData.total_referrals || 0
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End:: Referral Code Card -->

      <Tabs
        :tabsContent="tabsContent"
        :activeKey="activeTab"
        @getItems="onTabChange"
      >
        <!-- ======================== Referrals Log ======================== -->
        <template #referrals>
          <div class="tab_panel_wrapper">
            <div class="table_title_wrapper">
              <div class="title_text_wrapper">
                <h5>{{ $t("PLACEHOLDERS.referralsLog") }}</h5>
              </div>
            </div>

            <v-data-table
              class="thumb"
              :loading="referrals.loading"
              :loading-text="$t('TABLES.loadingData')"
              :headers="referralsHeaders"
              :items="referrals.rows"
              item-class="ltr"
              :items-per-page="referrals.paginations.items_per_page"
              hide-default-footer
            >
              <template v-slot:no-data>
                {{ $t("TABLES.noData") }}
              </template>

              <template v-slot:[`item.id`]="{ item, index }">
                <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">
                  -
                </p>
                <p v-else>
                  {{ getSerial(referrals.paginations, index) }}
                </p>
              </template>

              <template v-slot:[`item.joined_provider.name`]="{ item }">
                <p v-if="item.joined_provider && item.joined_provider.name">
                  {{ item.joined_provider.name }}
                </p>
                <p v-else class="blue-grey--text text--darken-1">-</p>
              </template>

              <template v-slot:[`item.created_at`]="{ item }">
                <p v-if="item.created_at">{{ item.created_at }}</p>
                <p v-else class="blue-grey--text text--darken-1">-</p>
              </template>

              <template v-slot:[`item.points`]="{ item }">
                <div class="points_badge points_badge--earn">
                  <i class="fal fa-star text-warning"></i>
                  <span>+{{ item.points || 0 }}</span>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="actions">
                  <a-tooltip placement="bottom">
                    <template slot="title">
                      <span>{{ $t("BUTTONS.show") }}</span>
                    </template>
                    <button
                      class="btn_show"
                      @click="showReferralItem(item)"
                    >
                      <i class="fal fa-eye"></i>
                    </button>
                  </a-tooltip>
                </div>
              </template>
            </v-data-table>

            <div class="pagination_container text-center mt-3 mb-0">
              <v-pagination
                class="py-0"
                square
                v-model="referrals.paginations.current_page"
                :length="referrals.paginations.last_page"
                :total-visible="6"
                @input="updateRouterQueryParam($event)"
                :prev-icon="
                  getAppLocale == 'ar'
                    ? 'fal fa-angle-right'
                    : 'fal fa-angle-left'
                "
                :next-icon="
                  getAppLocale == 'ar'
                    ? 'fal fa-angle-left'
                    : 'fal fa-angle-right'
                "
              />
            </div>
          </div>
        </template>

        <!-- ======================== Points Usage Log ======================== -->
        <template #usage>
          <div class="tab_panel_wrapper">
            <div class="table_title_wrapper">
              <div class="title_text_wrapper">
                <h5>{{ $t("PLACEHOLDERS.pointsUsageLog") }}</h5>
              </div>
            </div>

            <v-data-table
              class="thumb"
              :loading="usage.loading"
              :loading-text="$t('TABLES.loadingData')"
              :headers="usageHeaders"
              :items="usage.rows"
              item-class="ltr"
              :items-per-page="usage.paginations.items_per_page"
              hide-default-footer
            >
              <template v-slot:no-data>
                {{ $t("TABLES.noData") }}
              </template>

              <template v-slot:[`item.id`]="{ item, index }">
                <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">
                  -
                </p>
                <p v-else>
                  {{ getSerial(usage.paginations, index) }}
                </p>
              </template>

              <template v-slot:[`item.created_at`]="{ item }">
                <p v-if="item.created_at">{{ item.created_at }}</p>
                <p v-else class="blue-grey--text text--darken-1">-</p>
              </template>

              <template v-slot:[`item.points`]="{ item }">
                <div class="points_badge points_badge--spend">
                  <i class="fal fa-minus-circle"></i>
                  <span>-{{ Math.abs(item.points || 0) }}</span>
                </div>
              </template>

              <template v-slot:[`item.usage_details`]="{ item }">
                <p v-if="item.usage_details || item.details || item.description">
                  {{
                    item.usage_details || item.details || item.description
                  }}
                </p>
                <p v-else class="blue-grey--text text--darken-1">-</p>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="actions">
                  <a-tooltip placement="bottom">
                    <template slot="title">
                      <span>{{ $t("BUTTONS.show") }}</span>
                    </template>
                    <button class="btn_show" @click="showUsageItem(item)">
                      <i class="fal fa-eye"></i>
                    </button>
                  </a-tooltip>
                </div>
              </template>
            </v-data-table>

            <div class="pagination_container text-center mt-3 mb-0">
              <v-pagination
                class="py-0"
                square
                v-model="usage.paginations.current_page"
                :length="usage.paginations.last_page"
                :total-visible="6"
                @input="updateRouterQueryParam($event)"
                :prev-icon="
                  getAppLocale == 'ar'
                    ? 'fal fa-angle-right'
                    : 'fal fa-angle-left'
                "
                :next-icon="
                  getAppLocale == 'ar'
                    ? 'fal fa-angle-left'
                    : 'fal fa-angle-right'
                "
              />
            </div>
          </div>
        </template>
      </Tabs>

      <!-- Start:: Referral Details Modal -->
      <v-dialog v-model="dialogReferralDetails" max-width="600px">
        <v-card v-if="selectedReferral">
          <v-card-title class="text-h5 justify-center">
            {{ $t("PLACEHOLDERS.referralDetails") }}
          </v-card-title>
          <v-card-text>
            <div class="details_content">
              <div class="detail_row">
                <span class="detail_label"
                  >{{
                    $t("PLACEHOLDERS.joined_provider_name_referral")
                  }}:</span
                >
                <span class="detail_value">{{
                  selectedReferral.joined_provider?.name || "-"
                }}</span>
              </div>
              <div class="detail_row">
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.joiningDate") }}:</span
                >
                <span class="detail_value">{{
                  selectedReferral.created_at || "-"
                }}</span>
              </div>
              <div
                class="detail_row"
                v-if="selectedReferral.joined_provider?.email"
              >
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.email") }}:</span
                >
                <span class="detail_value">{{
                  selectedReferral.joined_provider.email
                }}</span>
              </div>
              <div
                class="detail_row"
                v-if="selectedReferral.joined_provider?.phone"
              >
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.phone") }}:</span
                >
                <span class="detail_value">{{
                  selectedReferral.joined_provider.phone
                }}</span>
              </div>
              <div class="detail_row">
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.earned_points") }}:</span
                >
                <span class="detail_value">
                  <div class="points_badge points_badge--earn">
                    <i class="fal fa-star text-warning"></i>
                    <span>+{{ selectedReferral.points || 0 }}</span>
                  </div>
                </span>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="modal_cancel_btn"
              @click="dialogReferralDetails = false"
            >
              {{ $t("BUTTONS.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- End:: Referral Details Modal -->

      <!-- Start:: Usage Details Modal -->
      <v-dialog v-model="dialogUsageDetails" max-width="600px">
        <v-card v-if="selectedUsage">
          <v-card-title class="text-h5 justify-center">
            {{ $t("PLACEHOLDERS.pointsUsageDetails") }}
          </v-card-title>
          <v-card-text>
            <div class="details_content">
              <div class="detail_row">
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.usageDate") }}:</span
                >
                <span class="detail_value">{{
                  selectedUsage.created_at || "-"
                }}</span>
              </div>
              <div class="detail_row">
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.deducted_points") }}:</span
                >
                <span class="detail_value">
                  <div class="points_badge points_badge--spend">
                    <i class="fal fa-minus-circle"></i>
                    <span>-{{ Math.abs(selectedUsage.points || 0) }}</span>
                  </div>
                </span>
              </div>
              <div class="detail_row">
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.usageDetails") }}:</span
                >
                <span class="detail_value">{{
                  selectedUsage.usage_details ||
                  selectedUsage.details ||
                  selectedUsage.description ||
                  "-"
                }}</span>
              </div>
              <div class="detail_row" v-if="selectedUsage.reference_type">
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.usageType") }}:</span
                >
                <span class="detail_value">{{
                  selectedUsage.reference_type
                }}</span>
              </div>
              <div class="detail_row" v-if="selectedUsage.reference_id">
                <span class="detail_label"
                  >{{ $t("PLACEHOLDERS.referenceId") }}:</span
                >
                <span class="detail_value">{{
                  selectedUsage.reference_id
                }}</span>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="modal_cancel_btn" @click="dialogUsageDetails = false">
              {{ $t("BUTTONS.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- End:: Usage Details Modal -->
    </main>
    <!-- End:: Main Section -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Tabs from "@/components/structure/Tabs.vue";

export default {
  name: "ProviderReferralCodeView",

  components: {
    Tabs,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    activeTab() {
      return this.$route.query.tab === "usage" ? "usage" : "referrals";
    },
  },

  data() {
    return {
      tabsContent: [
        {
          tab: this.$t("PLACEHOLDERS.referralsLog"),
          key_name: "referrals",
        },
        {
          tab: this.$t("PLACEHOLDERS.pointsUsageLog"),
          key_name: "usage",
        },
      ],

      referralCodeData: {
        code: "",
        total_points: 0,
        total_referrals: 0,
      },
      codeCopied: false,

      referralsHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.joined_provider_name_referral"),
          value: "joined_provider.name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.joiningDate"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.earned_points"),
          value: "points",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],

      usageHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.usageDate"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.deducted_points"),
          value: "points",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.usageDetails"),
          value: "usage_details",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],

      referrals: {
        loading: false,
        rows: [],
        paginations: {
          current_page: 1,
          last_page: 1,
          items_per_page: 6,
        },
      },

      usage: {
        loading: false,
        rows: [],
        paginations: {
          current_page: 1,
          last_page: 1,
          items_per_page: 6,
        },
      },

      dialogReferralDetails: false,
      selectedReferral: null,
      dialogUsageDetails: false,
      selectedUsage: null,
    };
  },

  watch: {
    ["$route.query.page"]() {
      const page = +this.$route.query.page || 1;
      if (this.activeTab === "usage") {
        this.usage.paginations.current_page = page;
        this.fetchUsageRows();
      } else {
        this.referrals.paginations.current_page = page;
        this.fetchReferralRows();
      }
    },
    ["$route.query.tab"]() {
      this.loadActiveTab();
    },
  },

  methods: {
    getSerial(paginations, index) {
      return (
        (paginations.current_page - 1 || 0) *
          (paginations.items_per_page || 0) +
        index +
        1
      );
    },

    async copyReferralCode() {
      try {
        const input = document.getElementById("referralCodeInput");
        input.select();
        document.execCommand("copy");

        this.codeCopied = true;
        this.$message.success(this.$t("PLACEHOLDERS.copiedSuccessfully"));

        setTimeout(() => {
          this.codeCopied = false;
        }, 2000);
      } catch (error) {
        this.$message.error(this.$t("PLACEHOLDERS.copyFailed"));
      }
    },

    onTabChange(tabKey) {
      if (tabKey === this.activeTab) return;

      this.$router.push({
        query: {
          ...this.$route.query,
          tab: tabKey,
          page: 1,
        },
      });
    },

    updateRouterQueryParam(paginationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          tab: this.activeTab,
          page: paginationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    applySummaryFromResponse(data = {}) {
      this.referralCodeData = {
        // parent account referral code
        code:
          data.code != null && data.code !== ""
            ? data.code
            : this.referralCodeData.code || "",
        total_points:
          data.total_points != null
            ? data.total_points
            : this.referralCodeData.total_points || 0,
        // always prefer backend total_referrals (not meta.total of usage page)
        total_referrals:
          data.total_referrals != null
            ? data.total_referrals
            : this.referralCodeData.total_referrals || 0,
      };
    },

    async fetchReferralRows() {
      this.referrals.loading = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "referral_provider-codes",
          params: {
            page: this.referrals.paginations.current_page,
            type: "referral",
          },
        });

        this.referrals.rows = res.data.data.referralProviderCodes || [];
        this.referrals.paginations.last_page =
          res.data.data?.meta?.last_page || 1;
        this.referrals.paginations.items_per_page =
          res.data.data?.meta?.per_page || 6;
        this.applySummaryFromResponse(res.data.data);
      } catch (error) {
        console.log(error.response?.data?.message);
        this.$message.error(error.response?.data?.message);
      } finally {
        this.referrals.loading = false;
      }
    },

    async fetchUsageRows() {
      this.usage.loading = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "referral_provider-codes",
          params: {
            page: this.usage.paginations.current_page,
            type: "usage",
          },
        });

        this.usage.rows = res.data.data.pointsUsages || [];
        this.usage.paginations.last_page =
          res.data.data?.meta?.last_page || 1;
        this.usage.paginations.items_per_page =
          res.data.data?.meta?.per_page || 6;
        this.applySummaryFromResponse(res.data.data);
      } catch (error) {
        console.log(error.response?.data?.message);
        this.$message.error(error.response?.data?.message);
      } finally {
        this.usage.loading = false;
      }
    },

    loadActiveTab() {
      const page = +this.$route.query.page || 1;
      if (this.activeTab === "usage") {
        this.usage.paginations.current_page = page;
        this.fetchUsageRows();
      } else {
        this.referrals.paginations.current_page = page;
        this.fetchReferralRows();
      }
    },

    showReferralItem(item) {
      this.selectedReferral = item;
      this.dialogReferralDetails = true;
    },

    showUsageItem(item) {
      this.selectedUsage = item;
      this.dialogUsageDetails = true;
    },
  },

  created() {
    const page = +this.$route.query.page || 1;
    if (this.activeTab === "usage") {
      this.usage.paginations.current_page = page;
    } else {
      this.referrals.paginations.current_page = page;
    }
    this.loadActiveTab();
  },
};
</script>

<style scoped>
.referral_code_card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card_header {
  background: linear-gradient(135deg, #1b706f 0%, #155e5e 100%);
  padding: 20px;
  color: #fff;
}

.card_header h5 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.card_body {
  padding: 30px;
}

.code_display_wrapper {
  margin-bottom: 20px;
}

.code_display_wrapper .form-label {
  font-weight: 600;
  margin-bottom: 10px;
  display: block;
}

.code_box {
  display: flex;
  gap: 10px;
  align-items: center;
}

.code_box .form-control {
  flex: 1;
  font-family: monospace;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-align: center;
  background: #f8f9fa;
  border: 2px solid #e0e0e0;
  padding: 12px;
  color: #1b706f;
}

.copy_btn {
  background: #1b706f;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;
}

.copy_btn:hover {
  background: #155e5e;
  transform: translateY(-2px);
}

.points_summary {
  padding: 0 20px;
}

.summary_item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.summary_text {
  display: flex;
  flex-direction: column;
}

.summary_text .label {
  font-size: 0.9rem;
  font-weight: 500;
}

.summary_text .value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1b706f;
}

.tab_panel_wrapper {
  padding: 16px;
}

.points_badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: 600;
}

.points_badge--earn {
  color: #1b706f;
  border: 1px solid #1b706f;
}

.points_badge--spend {
  color: #c0392b;
  border: 1px solid #c0392b;
}

.details_content {
  padding: 20px 0;
}

.detail_row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail_row:last-child {
  border-bottom: none;
}

.detail_label {
  font-weight: 600;
}

.detail_value {
  font-weight: 500;
  text-align: right;
}

@media (max-width: 768px) {
  .card_body {
    padding: 20px;
  }

  .points_summary {
    padding: 20px 0 0 0;
  }

  .code_box .form-control {
    font-size: 0.9rem;
    letter-spacing: 1px;
  }
}
</style>
